import { unwrap, type MemberOut, type RoleOut, type TeamOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Select, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { FormAlert } from "../auth/components/form-alert";
import { useErrorMessage } from "../lib/api-errors";
import { EffectivePermissions } from "../roles/effective-permissions";
import { ScopePicker, isScopeComplete, type ScopeValue } from "../roles/scope-picker";
import { useEffectivePreview, useInvalidateAccess } from "../roles/use-roles";
import { useApi } from "../session/session-provider";

interface Row extends ScopeValue {
  role_id: string;
}

interface MemberRolesDialogProps {
  member: MemberOut;
  roles: readonly RoleOut[];
  teams: readonly TeamOut[];
  totalPermissions: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function AssignmentRow({ row, index, roles, teams, onChange, onRemove }: { row: Row; index: number; roles: readonly RoleOut[]; teams: readonly TeamOut[]; onChange: (row: Row) => void; onRemove: () => void }) {
  const { t } = useTranslation();
  return (
    <li className="space-y-3 rounded-lg border border-line bg-surface-2 p-4">
      <div className="flex items-end gap-2">
        <Field label={t("team.chooseRole")} className="flex-1" id={`assignment-role-${String(index)}`}>
          <Select value={row.role_id} onChange={(event) => { onChange({ ...row, role_id: event.target.value }); }}>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </Select>
        </Field>
        <Button variant="ghost" iconOnly aria-label={`${t("team.removeAssignment")} ${String(index + 1)}`} onClick={onRemove}>
          <Icon name="trash" />
        </Button>
      </div>
      <ScopePicker value={row} onChange={(scope) => { onChange({ ...row, ...scope }); }} teams={teams} />
    </li>
  );
}

/** Edit all of a member's role assignments (each with its own scope) — union previewed before saving. */
export function MemberRolesDialog({ member, roles, teams, totalPermissions, open, onOpenChange }: MemberRolesDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateAccess();
  const [rows, setRows] = useState<Row[]>(() => member.assignments.map((a) => ({ role_id: a.role_id, scope_type: a.scope_type, scope_ids: a.scope_ids })));
  const complete = rows.length > 0 && rows.every(isScopeComplete);
  const preview = useEffectivePreview({ assignments: rows }, open && complete);
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/members/{membership_id}/roles", { params: { path: { membership_id: member.id } }, body: { assignments: rows } })),
    onSuccess: async () => {
      toast.success(t("team.rolesSaved", { name: member.name }));
      await invalidate();
      onOpenChange(false);
    },
  });
  const firstRole = roles.find((r) => r.key === "viewer") ?? roles[0];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t("team.rolesDialogTitle", { name: member.name })}
        description={t("team.rolesDialogBody")}
        closeLabel={t("common.close")}
        className="max-w-xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => { onOpenChange(false); }}>
              {t("team.cancel")}
            </Button>
            <Button loading={save.isPending} disabled={!complete || save.isPending} onClick={() => { save.mutate(); }}>
              {t("team.saveRoles")}
            </Button>
          </>
        }
      >
        <div className="max-h-[60vh] space-y-4 overflow-y-auto">
          {save.isError ? <FormAlert title={message(save.error)} onDismiss={() => { save.reset(); }} /> : null}
          <ul className="space-y-3">
            {rows.map((row, index) => (
              <AssignmentRow
                key={index}
                row={row}
                index={index}
                roles={roles}
                teams={teams}
                onChange={(next) => { setRows(rows.map((r, i) => (i === index ? next : r))); }}
                onRemove={() => { setRows(rows.filter((_, i) => i !== index)); }}
              />
            ))}
          </ul>
          {firstRole ? (
            <Button variant="secondary" size="sm" onClick={() => { setRows([...rows, { role_id: firstRole.id, scope_type: "all", scope_ids: [] }]); }}>
              <Icon name="plus" />
              {t("team.addAssignment")}
            </Button>
          ) : null}
          <div className="rounded-lg border border-line p-4">
            <EffectivePermissions
              preview={preview.data}
              isPending={preview.isFetching}
              isError={preview.isError}
              total={totalPermissions}
              title={t("roles.effective.memberTitle", { name: member.name })}
              subtitle={t("roles.effective.memberSubtitle")}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
